/**
 * <EdgeIntroScreen />
 *
 * Single-screen, single-CTA framing page used right before any major
 * tool inside THE EDGE world. Strict rules:
 *
 *   • One screen — no scrolling on any reasonable mobile viewport.
 *   • Centred title (serif), short sub, 3–5 lines of body, one CTA.
 *   • "Keep it simple." sits beneath the CTA, always.
 *   • Subtle ambient amber drift in the background (very light motion).
 *
 * Used as a universal wrapper — `<EdgeIntroScreen title=… body=… ctaTo=… />`
 * so the same component powers Living Vision, Bank of Reclaim, State
 * Resets, Private Suite, and Guided Performance intros.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function EdgeIntroScreen({
  eyebrow = "The Edge",
  title,
  sub,
  body,           // string OR array of strings (each = its own line)
  ctaLabel,
  ctaTo,
  onBack,         // optional override; defaults to navigate(-1)
  testId = "edge-intro",
}) {
  const navigate = useNavigate();
  const lines = Array.isArray(body) ? body : (body || "").split("\n").filter(Boolean);

  const handleBack = () => {
    if (onBack) return onBack();
    if (window.history.length > 1) navigate(-1);
    else navigate("/networker");
  };
  const handleEnter = () => navigate(ctaTo);

  return (
    <div
      className="fixed inset-0 overflow-hidden"
      data-testid={testId}
      style={{
        background:
          "radial-gradient(ellipse at 50% 28%, #1A1410 0%, #0E0B08 55%, #050402 100%)",
      }}
    >
      {/* Slow, very subtle amber drift */}
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(circle at 28% 22%, rgba(217,165,90,0.10) 0%, transparent 60%)",
            "radial-gradient(circle at 72% 32%, rgba(217,165,90,0.10) 0%, transparent 60%)",
            "radial-gradient(circle at 28% 22%, rgba(217,165,90,0.10) 0%, transparent 60%)",
          ],
        }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Top-left back chip */}
      <div className="absolute top-4 left-5 z-20">
        <button
          onClick={handleBack}
          data-testid={`${testId}-back-btn`}
          className="flex items-center gap-2 text-white/45 hover:text-white/90 transition-colors px-2 py-1.5"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-[10px] uppercase tracking-[0.4em]">{eyebrow}</span>
        </button>
      </div>

      {/* Centre stage */}
      <div className="relative h-full flex flex-col items-center justify-center px-7 max-w-xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0,  filter: "blur(0px)" }}
          transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
          className="w-full text-center"
        >
          <h1
            className="text-white text-[34px] sm:text-[42px] leading-tight font-medium mb-3"
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              letterSpacing: "-0.012em",
              textShadow: "0 6px 38px rgba(217,165,90,0.22)",
            }}
            data-testid={`${testId}-title`}
          >
            {title}
          </h1>

          {sub && (
            <p
              className="text-amber-200/85 text-[15px] uppercase tracking-[0.18em] mb-7"
              style={{ fontWeight: 500 }}
              data-testid={`${testId}-sub`}
            >
              {sub}
            </p>
          )}

          <div className="space-y-3.5 max-w-[36ch] mx-auto">
            {lines.map((line, idx) => (
              <p
                key={idx}
                className={`text-white/${idx === 0 ? 80 : 60} text-base sm:text-[17px] leading-relaxed`}
                style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontStyle: idx === 1 || idx === 2 ? "italic" : "normal",
                }}
              >
                {line}
              </p>
            ))}
          </div>
        </motion.div>
      </div>

      {/* CTA + "Keep it simple." anchored bottom */}
      <div className="absolute bottom-0 inset-x-0 px-7 pb-8 pt-12 z-20"
           style={{
             background:
               "linear-gradient(180deg, transparent, rgba(5,4,2,0.85) 55%, #050402)",
           }}>
        <div className="max-w-xl mx-auto flex flex-col items-center">
          <button
            onClick={handleEnter}
            data-testid={`${testId}-cta-btn`}
            className="w-full py-4 rounded-full text-white font-medium text-[14px] tracking-[0.06em] uppercase transition-all duration-300 hover:brightness-110"
            style={{
              background:
                "linear-gradient(135deg, rgba(217,165,90,0.95), rgba(180,120,60,0.95))",
              boxShadow:
                "0 14px 38px -12px rgba(217,165,90,0.65), inset 0 1px 0 rgba(255,255,255,0.10)",
            }}
          >
            <span className="inline-flex items-center gap-2 justify-center">
              {ctaLabel}
              <ArrowRight className="w-4 h-4" />
            </span>
          </button>
          <p className="mt-4 text-white/35 text-[10px] uppercase tracking-[0.45em]"
             style={{ fontWeight: 500 }}>
            Keep it simple.
          </p>
        </div>
      </div>
    </div>
  );
}
